import { z } from "zod";

const topErrorSchema = z.object({
	errorCode: z.string(),
	errorName: z.string(),
	count: z.number(),
	machineType: z.string().optional(),
});

const topErrorsByMachineSchema = z.object({
	machineName: z.string(),
	topErrors: z.array(topErrorSchema),
});

const averageErrorsPerMachinePerHourSchema = z.object({
	machineName: z.string(),
	count: z.number(),
});

const errorsBucketV1ResponseSchema = z.object({
	totalErrors: z.number().optional(),
	topErrors: z.array(topErrorSchema).optional(),
	topErrorsByMachine: z.array(topErrorsByMachineSchema).optional(),
	averageErrorsPerMachinePerHour: z.array(averageErrorsPerMachinePerHourSchema).optional(),
	isScheduleApplied: z.boolean().optional(),
});

const availabilityByMachineSchema = z.object({
	availability: z.number(),
	machineName: z.string(),
});

const availabilityByStatusSchema = z.object({
	status: z.string(),
	percentage: z.number(),
});

const breakdownMachineStatusSchema = z.object({
	breakdownStatus: z.string(),
	normalizedStatus: z.string(),
	percentage: z.number(),
	count: z.number().optional(),
});

const availabilityByStatusDetailedSchema = z.object({
	status: z.string(),
	percentage: z.number(),
	breakdownStatuses: z.array(breakdownMachineStatusSchema).optional(),
});

const statusDataPointSchema = z.object({
	name: z.string(),
	data: z.array(z.number()),
});

const machineStatusByHourDataSchema = z.object({
	machineId: z.string(),
	machineName: z.string(),
	data: z.array(statusDataPointSchema),
});

const availabilityBucketsSchema = z.object({
	availabilityScore: z.number().optional(),
	byMachine: z.array(availabilityByMachineSchema).optional(),
	byStatus: z.array(availabilityByStatusSchema).optional(),
	byStatusDetailed: z.array(availabilityByStatusDetailedSchema).optional(),
	byMachineByHour: z.array(machineStatusByHourDataSchema).optional(),
	isScheduleApplied: z.boolean().optional(),
});

export const errorsAvailabilityQuarterlyResponseSchema = z.object({
	errorsBucketV1Responses: errorsBucketV1ResponseSchema,
	availabilityBuckets: availabilityBucketsSchema,
	isScheduleApplied: z.boolean(),
});

export const stopReasonSchema = z.object({
	stopReason: z.string(),
	count: z.number(),
	durationInMinutes: z.number(),
	stopCode: z.number(),
});

export const stopReasonsApiResponseSchema = z.object({
	stopReasons: z.array(stopReasonSchema).optional(),
});

export const failureReasonSchema = z.object({
	failureReason: z.string(),
	count: z.number(),
});

export const failureReasonsApiResponseSchema = z.object({
	failureReasons: z.array(failureReasonSchema).optional(),
});

export type TopError = z.infer<typeof topErrorSchema>;
export type TopErrorsByMachine = z.infer<typeof topErrorsByMachineSchema>;
export type AverageErrorsPerMachinePerHour = z.infer<typeof averageErrorsPerMachinePerHourSchema>;
export type AvailabilityByStatus = z.infer<typeof availabilityByStatusSchema>;
export type MachineStatusByHourData = z.infer<typeof machineStatusByHourDataSchema>;
export type ErrorsAvailabilityQuarterlyResponse = z.infer<typeof errorsAvailabilityQuarterlyResponseSchema>;
export type StopReason = z.infer<typeof stopReasonSchema>;
export type StopReasonsApiResponse = z.infer<typeof stopReasonsApiResponseSchema>;
export type FailureReason = z.infer<typeof failureReasonSchema>;
export type FailureReasonsApiResponse = z.infer<typeof failureReasonsApiResponseSchema>;

export const DEFAULT_ERRORS_AVAILABILITY_RESPONSE: ErrorsAvailabilityQuarterlyResponse = {
	errorsBucketV1Responses: {
		totalErrors: 0,
		topErrors: [],
		topErrorsByMachine: [],
		averageErrorsPerMachinePerHour: [],
		isScheduleApplied: false,
	},
	availabilityBuckets: {
		availabilityScore: 0,
		byMachine: [],
		byStatus: [],
		byMachineByHour: [],
		isScheduleApplied: false,
	},
	isScheduleApplied: false,
};

export interface MachineAvailabilityData {
	machineName: string;
	machineId?: string;
	availability: number;
	plannedProductionTime?: number;
	actualRunTime?: number;
	downtime?: number;
	percentage?: number;
}
